import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  StatusBar,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector, useAppDispatch } from '@/store';
import { refreshUser } from '@/store/slices/authSlice';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { PageHeader, GradientBackground } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';


import { MobileAccountInfoSection } from './sections/MobileAccountInfoSection';
import { MobileResetPasswordSection } from './sections/MobileResetPasswordSection';
import { MobilePaymentMethodsSection } from './sections/MobilePaymentMethodsSection';
import { MobileAutopaySection } from './sections/MobileAutopaySection';
import { MobileNotificationSettingsSection } from './sections/MobileNotificationSettingsSection';

export type AccountSectionKey = 'accountInfo' | 'paymentMethods' | 'autopay' | 'notificationSettings' | 'resetPassword';

const SECTION_META: Record<AccountSectionKey, { title: string; subtitle: string }> = {
  accountInfo: { title: 'Account Information', subtitle: 'Name and email' },
  paymentMethods: { title: 'Payment Methods', subtitle: 'View, add, or remove cards' },
  autopay: { title: 'Autopay', subtitle: 'Manage automatic payments' },
  notificationSettings: { title: 'Notifications', subtitle: 'Stay in the loop' },
  resetPassword: { title: 'Reset Password', subtitle: 'Secure your account' },
};

export const MobileAccountScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();

  const { user } = useAppSelector((state) => state.auth);

  const [activeSection, setActiveSection] = useState<AccountSectionKey>('accountInfo');

  const {
    handleSignOut,
  } = useResidentScreen('MobileAccountSettings', navigation);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  useEffect(() => {
    const sectionParam = route?.params?.section as AccountSectionKey | undefined;
    if (sectionParam) {
      setActiveSection(sectionParam);
    }
  }, [route?.params?.section]);

  useFocusEffect(
    useCallback(() => {
      dispatch(refreshUser());
    }, [dispatch])
  );

  const handleAccountDeleted = useCallback(() => {
    handleSignOut();
  }, [handleSignOut]);

  const meta = SECTION_META[activeSection];

  const renderContent = () => {
    switch (activeSection) {
      case 'accountInfo':
        return <MobileAccountInfoSection onAccountDeleted={handleAccountDeleted} />;
      case 'paymentMethods':
        return <MobilePaymentMethodsSection />;
      case 'autopay':
        return <MobileAutopaySection />;
      case 'notificationSettings':
        return <MobileNotificationSettingsSection />;
      case 'resetPassword':
        return <MobileResetPasswordSection />;
      default:
        return <MobileAccountInfoSection onAccountDeleted={handleAccountDeleted} />;
    }
  };

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="MobileAccountSettings"
        darkBackground
        headerVariant="gradient"
      >
        <View style={styles.backLinkWrapper}>
          <BackLink
            label="Back to settings"
            onPress={handleBack}
            color={PRIMITIVE_COLORS.resioBlue}
          />
        </View>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
          showsVerticalScrollIndicator={false}
        >
          <PageHeader title={meta.title} subtitle={meta.subtitle} />

          <View style={styles.contentSection}>
            {renderContent()}
          </View>
        </ScrollView>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const HP = REDESIGN_SPACING.horizontalPadding;

const styles = StyleSheet.create({
  backLinkWrapper: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
  },
  contentSection: {
    flex: 1,
  },
});

export default MobileAccountScreen;
